import React, { useState, useEffect } from 'react';
import './App.css'; // We'll create this CSS file for styling

// Import the logo image
import splashLogo from './images/bennetts-daily-murder-mysteries.png';

// Import our components
import { 
  TextMessage, 
  TextConversation,
  Note, 
  SearchHistory, 
  BankTransaction, 
  LocationData,
  StoryRenderer,
  InvestigationRenderer,
  SolutionRenderer,
  Email,
  Voicemail
} from './components';

// Splash Screen Component
const SplashScreen = ({ onStart }) => {
    // Get today's date in a nice format
    const formatDate = () => {
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return new Date().toLocaleDateString('en-US', options);
    };

    return (
        <div className="splash-screen">
            <div className="splash-content">
                <img 
                    src={splashLogo} 
                    alt="Bennett's Daily Murder Mysteries" 
                    className="splash-logo"
                />
                <h2 className="splash-date">{formatDate()}</h2>
                <div className="buttons">
                    <button onClick={onStart} className="start-button">
                        Solve Today's Mystery
                    </button>
                </div>
            </div>
        </div>
    );
};

// Raw story text directly embedded
const rawStoryText = `[title]The Case of the Calamitous Croquembouche and the Candid Confessions[/title]

[story]
The Maple Creek Annual Summer Bake-Off. My "Majestic Mocha Marvel" was a contender, I just knew it. This year felt different.

The community hall buzzed. Martha Grimshaw, our indefatigable PTA President, darted about, clipboard in hand, her bun a tight knot of efficiency. She was the town’s engine, a pillar of Maple Creek. Arthur Peabody, our gentle librarian, was already fussing over his ambitious croquembouche. Sweet man, but high-pressure baking wasn't his forte. Young Gary "Gaz" Perkins, clumsy but eager, was wrestling with entry flags.

I’d just set up when Gaz rushed over, flushed. "Brenda, Mrs. Albright! We're out of the community vanilla. But my sister, she’s in a culinary program, had this… artisanal one." He held out a small, unlabeled dark bottle. "Super concentrated!"

"A lifesaver, Gary!" I took it. Artisanal? Sounded promising. It smelled of vanilla, with a sharp, chemical undertone. I only needed a touch for my emergency whipped cream.

By ten-thirty, the hall was a gallery of confections. I saw a few contestants eyeing the communal supply table where Gaz had placed the dark bottle after I’d used it.

The first sign something was amiss came when judge Mr. Abernathy, after tasting Mrs. Henderson’s fruitcake, boomed, "Good heavens, Agnes, this isn't 'interestingly textured,' it's like chewing on a fossilized tea cosy!" He looked horrified at his own words.

Then it was my turn. After sampling Betty Pringle’s Lemon Lush, I blurted, "Betty, dear, that hat makes you look like a startled cockatoo, but your lemon bars are surprisingly… edible today!" Mortification washed over me.

A wave of bizarre candor swept through one section of the hall. Mrs. Gable told the Mayor his breath smelled "faintly of disappointment." Little Timmy Evans declared his mother's cupcakes "soggy." No one connected it to a specific ingredient yet; just a strange, localized outbreak of truth.

Then, Arthur Peabody. He’d sampled several entries near that communal table. Suddenly, he stood, his face flushed, eyes blazing. "Martha Grimshaw!" his voice, usually a murmur, rang out. "For three years, you have been systematically embezzling funds from the Parent Teacher Association! The missing bake sale proceeds? The 'misfiled' charity auction donations? I saw the duplicate ledger in your desk!"

Silence. Martha, at the far end of the hall, spun around, her face ashen. Terror, or fury, flickered in her eyes before devastation settled. "Arthur… how dare you?" she whispered.
"The truth, Martha!" Arthur declared, then faltered, the enormity of his outburst hitting him. Martha fled the hall, shoulders shaking.

Murmurs erupted. Embezzlement? Martha? Unthinkable. Or was it? Her expensive handbags, her husband Tom’s lack of steady work…

The "truth-telling" chaos was soon overshadowed. Twenty minutes later, Millie Perkins, Gaz’s mother, screamed from the prep tent.
Arthur Peabody lay on the floor, his croquembouche scattered. Beside his head, a heavy wooden rolling pin, stained dark red.

Pandemonium. My blue ribbon dreams vanished. Arthur was murdered. At a bake-off.
The next hours were a blur. Gaz, guilt-ridden, confessed the "artisanal" vanilla was his sister’s experimental flavor compound – an unintended truth serum.

Poor Martha. Accused so brutally, then this. I saw her later, shattered, her usually immaculate bun loose. Not the face of a killer. Just… destroyed. Whispers started, of course: Arthur accuses Martha; Arthur dies. An ugly, simple equation. But Martha? The woman who organized the town’s daffodil planting?

I vaguely recalled seeing Martha slip back into the marquee from the prep tent’s direction, before Millie found Arthur, her face pale, clutching her handbag. I’d assumed she was composing herself.
The rolling pin was wiped clean. Common kitchen tool, accessible to many.

That vanilla had stripped away our polite pretenses. Maybe it revealed something someone couldn't bear to see the light of day. They say stress makes people do terrible things. Martha was under terrible stress. It’s a shadow over Maple Creek now.
[/story]

[additional-context]
Little details linger. Gaz’s sister, a chemistry prodigy, had theorized about psychoactive side effects if her experimental vanillin compound (VX-7) wasn't perfectly stable. Gaz, trying to be helpful, grabbed it from her workbench.

Martha Grimshaw. Her husband Tom’s "employment challenges" and expensive antique map hobby were a politely ignored secret. I’d always suspected Martha carried their financial weight. If she had skimmed, perhaps it was desperation, not greed. A week before, I saw her at the supermarket, looking exhausted, buying economy brands, while Tom idly flipped through a magazine. She was a woman stretched thin.

Arthur, a lover of local history, could have stumbled upon discrepancies in PTA records. He wasn't malicious. The vanilla forced his hand; normally, he’d have agonized, perhaps written an anonymous letter.

The prep tent was a thoroughfare. Mrs. Henderson, Betty Pringle, even I had been in and out. The rolling pin was on a communal rack.
I overheard Chief Brody mention defensive bruises on Arthur’s hands and a tear on his shirt sleeve, snagged on something sharp. "A button? Cufflink?"

A cufflink. Tom Grimshaw always wore them. He wasn't at the bake-off, Martha said he found crowds "overwhelming." But the prep tent backed onto the alley near the parking lot. The day before, I’d seen Martha and Tom in a tense, hushed argument in the town square. Martha looked frantic; Tom, dismissive, gestured sharply with a cuffed hand.

It’s all speculation. But picturing Martha, our stern PTA president, wielding a rolling pin… it’s almost impossible. She loved this town. Perhaps she was just a woman in shock, wrongly accused, caught in a horrifying orbit.
[/additional-context]

[suspect]
[name]Martha Grimshaw[/name]
[investigation]
Investigator: Mrs. Grimshaw, this is difficult. Mr. Peabody’s accusation…
Martha Grimshaw: (Voice tight) Vile. A public humiliation. Arthur wasn’t cruel. That… substance…
Investigator: Did you go into the prep tent after his outburst?
Martha Grimshaw: I… I had to get away. I think I did. To collect myself.
Investigator: See Mr. Peabody there?
Martha Grimshaw: (Hesitates) No. I don’t think so. I grabbed my purse and came straight out.
Investigator: The PTA funds. The duplicate ledger.
Martha Grimshaw: (Face hardens) Slander. Arthur was mistaken. Our books are impeccable. My husband, Tom… has expenses. Collections. Things get tight. But I would never steal.
Investigator: Financial pressure has been significant?
Martha Grimshaw: (Looks away) Every family has ups and downs. We manage.
[/investigation]
[/suspect]

[suspect]
[name]Brenda Albright[/name]
[investigation]
Investigator: Mrs. Albright, you noticed the strange effects of some baked goods?
Brenda Albright: (Nervous) Yes. After my… cockatoo comment about Betty’s hat. Then Mr. Abernathy. Something was wrong.
Investigator: You used Gaz Perkins’ experimental vanilla?
Brenda Albright: A tiny bit! For whipped cream. Gaz said it was artisanal. I didn’t put the bottle on the communal table; Gaz did that after I’d used a drop.
Investigator: You saw Mr. Peabody accuse Mrs. Grimshaw?
Brenda Albright: Yes. Shocking. Arthur, so quiet, booming. Martha looked… struck by lightning.
Investigator: In the prep tent around then?
Brenda Albright: Briefly, before Millie screamed. For a damp cloth. Didn't see Arthur.
Investigator: Mrs. Grimshaw looked distraught?
Brenda Albright: Broken. And just before Millie found Arthur, I saw Martha coming from the prep tent’s direction, face white, clutching her handbag. I thought she was still reeling. The look on her face when Arthur accused her… not just shock. Terror, maybe.
[/investigation]
[/suspect]

[suspect]
[name]Gary "Gaz" Perkins[/name]
[investigation]
Investigator: Gary, about the vanilla?
Gaz Perkins: (Voice cracking) M-my sister’s. VX-7. Her chemistry stuff. I thought it was fancy vanilla Mom bought. We were out. I put it on the communal table after Mrs. Albright used a bit. Didn’t mean for…
Investigator: Side effects?
Gaz Perkins: (Nods miserably) She said if it wasn't stable, it could make people talk more. Didn’t think it would actually happen. I didn’t know!
Investigator: See Mr. Peabody after the accusation?
Gaz Perkins: N-no. I was hiding. Everyone looked at me funny.
Investigator: Near the prep tent?
Gaz Perkins: Ducked in for a second. Before Mom screamed. Didn’t see anything.
Investigator: See Mrs. Grimshaw go in or out?
Gaz Perkins: (Thinks) After Mr. Peabody shouted, she looked… real scary. Saw her go towards the back. This is all my fault, isn't it?
[/investigation]
[/suspect]

[suspect]
[name]Tom Grimshaw[/name]
[investigation]
Investigator: Mr. Grimshaw, your wife is distraught.
Tom Grimshaw: (Sighs, rumpled suit, gleaming cufflinks) Martha’s strong. This is monstrous. Poor Peabody.
Investigator: You weren't at the bake-off?
Tom Grimshaw: No. Crowds aren't my scene. Pottering at home. Cataloging acquisitions.
Investigator: Acquisitions?
Tom Grimshaw: (Faint smile) Antique maps. Passion of mine. Doesn't pay the bills. Martha… concerns herself.
Investigator: Did your wife mention worries about PTA funds, or Mr. Peabody?
Tom Grimshaw: Martha worries. Wants perfection. PTA funds are her domain. Peabody? Never caused a ripple.
Investigator: You went to the hall later?
Tom Grimshaw: (Frowns) Ah, yes. Martha forgot migraine medication. Met her by the side entrance, near parking. Didn't go in.
Investigator: Near the prep tent. See Mr. Peabody?
Tom Grimshaw: Can’t say I did. Chaotic. Gave Martha her pills, quick word, left.
Investigator: A heated discussion with your wife the day before?
Tom Grimshaw: (Dismissive wave) Domestic trivialities. Hydrangeas, probably. Martha was stressed about the bake-off.
Investigator: Mr. Peabody’s accusation about a duplicate ledger.
Tom Grimshaw: (Scoffs) Preposterous. Martha is honest. Peabody was addled.
[/investigation]
[/suspect]

[solution]
The vanilla. That’s what they’ll blame. If it hadn’t been for that strange tang in Agnes Periwinkle’s scone, those words about the ledger would never have escaped my lips so… publicly. I’d been wrestling with it for weeks. The discrepancy. Not Martha’s neat, public PTA ledger, but the other, tucked in her archive box. Little sums, redirected. The transfers were to "T.G. Antiquarian Services." Tom Grimshaw. He had been systematically bleeding the PTA funds to finance his map collecting habit, a secret Martha clearly bore the financial and emotional cost of, likely without knowing the true, criminal extent of his "hobby."

I’d planned to speak to Martha privately. But the vanilla… it bypassed caution.
When I saw Martha’s face… devastation. She likely knew Tom was a financial drain, perhaps even suspected his methods weren't entirely above board, but the public accusation of her being an embezzler, stemming from his actions, was a fresh, brutal wound. She fled. I felt a pang of regret.

I retreated to the prep tent, head spinning. I heard footsteps. Heavier than Martha’s.
"Peabody," a voice said. Tom Grimshaw. I was surprised; I'd heard he wasn't attending.
"Mr. Grimshaw," I managed. He looked… cold.
"You’ve caused quite a stir, librarian," he said, stepping closer, something behind his back. "Sticking your nose where it doesn’t belong."
"The money, Mr. Grimshaw," I said. "Martha works so hard. The children…"
He laughed, ugly. "Martha works. I spend. Until busybodies upset the ecosystem." He took another step. I smelled stale cigarettes.
"She’ll find out the truth about what you’ve been doing," I said.
"Oh, I think not," he said, and his hand came from behind his back. The heavy wooden rolling pin. "Martha will believe what she needs to. You’ll be a casualty of unfortunate baking."
I raised my hands. The gleam of his cufflink as his arm swung. Then, silence. He thought he was clever, framing Martha. The public accusation against her, fueled by his secret thievery, would make her look guilty. He needed me silenced before I could show Martha the full, damning proof that he was the embezzler, that the "duplicate ledger" I’d found inadvertently detailed his systematic theft, which she was unknowingly, and now publicly, shouldering the blame for. He needed to stop me before I could clear her name by exposing his.
[/solution]

[murderer]Tom Grimshaw[/murderer]`;

// --- Helper Functions for Parsing ---

const extractTagContent = (text, tagName) => {
  const regex = new RegExp(`\\[${tagName}\\](.*?)\\[/${tagName}\\]`, 's');
  const match = text.match(regex);
  return match ? match[1].trim() : null;
};

// Function to extract emails from content
const extractEmails = (text) => {
    const emails = [];
    const emailRegex = /\[email\](.*?)\[\/email\]/gs;
    let match;
    
    while ((match = emailRegex.exec(text)) !== null) {
        const emailBlock = match[1];
        const from = extractTagContent(emailBlock, 'from');
        const to = extractTagContent(emailBlock, 'to');
        const subject = extractTagContent(emailBlock, 'subject');
        const timestamp = extractTagContent(emailBlock, 'timestamp');
        const status = extractTagContent(emailBlock, 'status');
        const body = extractTagContent(emailBlock, 'body');
        
        if (from && to && body) {
            emails.push({
                type: 'email',
                from,
                to,
                subject: subject || '(No Subject)',
                timestamp: timestamp || '',
                status: status || '',
                body,
                fullTag: match[0] // Store the full tag to remove it later
            });
        }
    }
    return emails;
};

// Function to extract text messages from content
const extractTextMessages = (text) => {
    const messages = [];
    const messageRegex = /\[text-message\](.*?)\[\/text-message\]/gs;
    let match;
    
    while ((match = messageRegex.exec(text)) !== null) {
        const messageBlock = match[1];
        const sender = extractTagContent(messageBlock, 'sender');
        const recipient = extractTagContent(messageBlock, 'recipient');
        const timestamp = extractTagContent(messageBlock, 'timestamp');
        const content = extractTagContent(messageBlock, 'content');
        
        if (sender && recipient && timestamp && content) {
            // Debug log to check message structure
            console.log(`Extracted message: Sender=${sender}, Recipient=${recipient}`);
            
            messages.push({
                type: 'text-message',
                sender,
                recipient,
                timestamp,
                content,
                fullTag: match[0] // Store the full tag to remove it later
            });
        }
    }
    return messages;
};

// Function to extract notes from content
const extractNotes = (text) => {
    const notes = [];
    const noteRegex = /\[note\](.*?)\[\/note\]/gs;
    let match;
    
    while ((match = noteRegex.exec(text)) !== null) {
        const noteBlock = match[1];
        const author = extractTagContent(noteBlock, 'author');
        const medium = extractTagContent(noteBlock, 'medium');
        const timestamp = extractTagContent(noteBlock, 'timestamp');
        const content = extractTagContent(noteBlock, 'content');
        
        if (author && content) {
            notes.push({
                type: 'note',
                author,
                medium: medium || '',
                timestamp: timestamp || '',
                content,
                fullTag: match[0]
            });
        }
    }
    return notes;
};

// Function to extract search history
const extractSearchHistory = (text) => {
    const searches = [];
    const searchRegex = /\[search-history\](.*?)\[\/search-history\]/gs;
    let match;
    
    while ((match = searchRegex.exec(text)) !== null) {
        const searchBlock = match[1];
        const user = extractTagContent(searchBlock, 'user');
        const timestamp = extractTagContent(searchBlock, 'timestamp');
        const query = extractTagContent(searchBlock, 'query');
        
        if (user && query) {
            searches.push({
                type: 'search-history',
                user,
                timestamp: timestamp || '',
                query,
                fullTag: match[0]
            });
        }
    }
    return searches;
};

// Function to extract bank transactions
const extractBankTransactions = (text) => {
    const transactions = [];
    const transactionRegex = /\[bank-transaction\](.*?)\[\/bank-transaction\]/gs;
    let match;
    
    while ((match = transactionRegex.exec(text)) !== null) {
        const transactionBlock = match[1];
        const accountHolder = extractTagContent(transactionBlock, 'account-holder');
        const timestamp = extractTagContent(transactionBlock, 'timestamp');
        const type = extractTagContent(transactionBlock, 'type');
        const amount = extractTagContent(transactionBlock, 'amount');
        const locationMerchant = extractTagContent(transactionBlock, 'location-merchant');
        
        if (accountHolder && amount) {
            transactions.push({
                type: 'bank-transaction',
                accountHolder,
                timestamp: timestamp || '',
                transactionType: type || '',
                amount,
                locationMerchant: locationMerchant || '',
                fullTag: match[0]
            });
        }
    }
    return transactions;
};

// Function to extract location data
const extractLocationData = (text) => {
    const locations = [];
    const locationRegex = /\[location-data\](.*?)\[\/location-data\]/gs;
    let match;
    
    while ((match = locationRegex.exec(text)) !== null) {
        const locationBlock = match[1];
        const device = extractTagContent(locationBlock, 'device');
        const timestamp = extractTagContent(locationBlock, 'timestamp');
        const location = extractTagContent(locationBlock, 'location');
        
        if (device && location) {
            locations.push({
                type: 'location-data',
                device,
                timestamp: timestamp || '',
                location,
                fullTag: match[0]
            });
        }
    }
    return locations;
};

// Function to extract voicemails from content
const extractVoicemails = (text) => {
    const voicemails = [];
    const voicemailRegex = /\[voicemail\](.*?)\[\/voicemail\]/gs;
    let match;
    
    while ((match = voicemailRegex.exec(text)) !== null) {
        const voicemailBlock = match[1];
        const caller = extractTagContent(voicemailBlock, 'caller');
        const recipient = extractTagContent(voicemailBlock, 'recipient');
        const timestamp = extractTagContent(voicemailBlock, 'timestamp');
        const transcript = extractTagContent(voicemailBlock, 'transcript');
        
        if (caller && recipient && transcript) {
            voicemails.push({
                type: 'voicemail',
                caller,
                recipient,
                timestamp: timestamp || '',
                transcript,
                fullTag: match[0] // Store the full tag to remove it later
            });
        }
    }
    return voicemails;
};

// Combined function to extract all evidence types
const extractAllEvidence = (text) => {
    if (!text) return { evidence: [], cleanedText: '' };
    
    // Extract all types of evidence
    const textMessages = extractTextMessages(text);
    const notes = extractNotes(text);
    const searches = extractSearchHistory(text);
    const transactions = extractBankTransactions(text);
    const locations = extractLocationData(text);
    const emails = extractEmails(text);
    const voicemails = extractVoicemails(text); // Add voicemails extraction
    
    // Combine all evidence types into a single array
    const allEvidence = [
        ...textMessages,
        ...notes,
        ...searches,
        ...transactions,
        ...locations,
        ...emails,
        ...voicemails // Add voicemails to all evidence
    ];
    
    // Create a cleaned version of the text with all evidence tags replaced by position markers
    let cleanedText = text;
    
    // First replace all specific evidence items with position markers
    if (allEvidence.length > 0) {
        allEvidence.forEach((item, index) => {
            if (item.fullTag) {
                // Replace each evidence tag with a position marker
                cleanedText = cleanedText.replace(item.fullTag, `[EVIDENCE_ITEM_${index}]`);
                
                // Store the index for reference
                item.index = index;
            }
        });
    }
    
    // Additional cleanup to catch any remaining tags that weren't extracted as evidence
    // Remove any text-message tags
    cleanedText = cleanedText.replace(/\[text-message\].*?\[\/text-message\]/gs, '');
    
    // Remove any note tags
    cleanedText = cleanedText.replace(/\[note\].*?\[\/note\]/gs, '');
    
    // Remove any search-history tags
    cleanedText = cleanedText.replace(/\[search-history\].*?\[\/search-history\]/gs, '');
    
    // Remove any bank-transaction tags
    cleanedText = cleanedText.replace(/\[bank-transaction\].*?\[\/bank-transaction\]/gs, '');
    
    // Remove any location-data tags (improved to catch cases where tags may be nested incorrectly)
    cleanedText = cleanedText.replace(/\[location-data\].*?\[\/location-data\]/gs, '');
    
    // Remove any email tags
    cleanedText = cleanedText.replace(/\[email\].*?\[\/email\]/gs, '');
    
    // Remove any voicemail tags
    cleanedText = cleanedText.replace(/\[voicemail\].*?\[\/voicemail\]/gs, '');
    
    // Remove any stray closing location-data tags that might remain
    cleanedText = cleanedText.replace(/\[\/location-data\]/g, '');
    
    // Remove nested property tags that might remain
    cleanedText = cleanedText.replace(/\[(sender|recipient|timestamp|content|author|medium|user|query|account-holder|type|amount|location-merchant|device|location|from|to|subject|status|body|caller|transcript)\].*?\[\/\1\]/gs, '');
    
    // Remove any stray opening property tags that remain
    cleanedText = cleanedText.replace(/\[(sender|recipient|timestamp|content|author|medium|user|query|account-holder|type|amount|location-merchant|device|location|from|to|subject|status|body|caller|transcript)\]/g, '');
    
    // Remove any stray closing property tags that remain
    cleanedText = cleanedText.replace(/\[\/(sender|recipient|timestamp|content|author|medium|user|query|account-holder|type|amount|location-merchant|device|location|from|to|subject|status|body|caller|transcript)\]/g, '');
    
    // Clean up excessive whitespace between paragraphs (keep at most 2 newlines)
    cleanedText = cleanedText.replace(/\n{3,}/g, '\n\n');

    // Remove leading and trailing newlines
    cleanedText = cleanedText.replace(/^\n+|\n+$/g, '');
    
    return {
        evidence: allEvidence,
        cleanedText: cleanedText.trim()
    };
};

const extractAllSuspects = (text) => {
    const suspects = [];
    const suspectRegex = /\[suspect\](.*?)\[\/suspect\]/gs;
    let match;
    while ((match = suspectRegex.exec(text)) !== null) {
        const suspectBlock = match[1];
        const name = extractTagContent(suspectBlock, 'name');
        const investigationText = extractTagContent(suspectBlock, 'investigation');
        
        if (name && investigationText) {
            // Extract all evidence from the investigation text
            const extractedData = extractAllEvidence(investigationText);
            
            suspects.push({ 
                name, 
                investigation: extractedData.cleanedText,
                evidence: extractedData.evidence
            });
        }
    }
    return suspects;
};

const extractClue = (text) => {
    // Matches: (Word before tag) optional space [clue] (Clue content) [/clue]
    const clueRegex = /(\w+)\s*\[clue\](.*?)\[\/clue\]/s;
    const match = text.match(clueRegex);
    if (match) {
        return {
            word: match[1],
            text: match[2].trim(),
            fullTag: match[0] // Store the full tag to remove it later
        };
    }
    return null;
};

// Update parseStory to use extractAllEvidence
const parseStory = (storyText) => {
  // Extract the title first
  const title = extractTagContent(storyText, 'title') || 'Mystery';
  
  // Extract the main story content
  const mainStoryContent = extractTagContent(storyText, 'story') || '';
  
  // Extract additional context if present
  const additionalContext = extractTagContent(storyText, 'additional-context') || '';
  
  // Extract all suspects
  const suspects = extractAllSuspects(storyText);
  
  // Extract clue if present
  const clue = extractClue(storyText);
  
  // Extract all evidence from the suspects' investigations
  // This variable is unused, commenting out to fix ESLint warning
  // const suspectEvidence = suspects.flatMap(suspect =>
  //  suspect.evidence ? suspect.evidence : []
  // );
  
  // Extract evidence directly from the main story
  const mainTextEvidence = extractAllEvidence(mainStoryContent);
  
  // Solution section
  let solution = '';
  let solutionEvidence = [];
  let murderer = '';
  
  // Extract solution if present
  const solutionText = extractTagContent(storyText, 'solution');
  if (solutionText) {
    const solutionData = extractAllEvidence(solutionText);
    solution = solutionData.cleanedText;
    solutionEvidence = solutionData.evidence;
    
    // Extract murderer if present
    murderer = extractTagContent(storyText, 'murderer') || '';
  }

  return {
    title,
    mainText: mainTextEvidence.cleanedText,
    additionalContext,
    suspects,
    clue,
    mainEvidence: mainTextEvidence.evidence,
    solution,
    solutionEvidence,
    murderer
  };
};

// Helper function to trim paragraphs while preserving intentional line breaks
// This function is unused, commenting out to fix ESLint warning
// const trimParagraph = (text) => {
//   // Limit paragraph length and add ellipsis if too long
//   if (text.length > 250) {
//     return text.substring(0, 250) + '...';
//   }
//   return text;
// };

// Evidence wrapper component that determines which specific evidence component to render
// eslint-disable-next-line no-unused-vars
const EvidenceItem = ({ item, items }) => {
    // If this is a text message and we have multiple items, render them as a group
    if (item.type === 'text-message' && items && items.length > 0) {
        // Find all consecutive text messages
        const textMessages = items.filter(i => i.type === 'text-message');
        
        // Only render for the first text message to avoid duplicates
        if (item !== textMessages[0]) {
            return null;
        }
        
        // Render the text message group
        return (
            <div className="text-messages-section">
                <h3 className="text-messages-header">Text Messages</h3>
                {textMessages.map((msg, idx) => (
                    <TextMessage key={`msg-${idx}`} message={msg} />
                ))}
                <div className="clearfix"></div>
            </div>
        );
    }
    
    // Handle other evidence types
    switch (item.type) {
        case 'text-message':
            // Individual text message (not part of a group)
            return <TextConversation messages={[item]} title="Text Message" />;
        case 'note':
            return <Note note={item} />;
        case 'search-history':
            return <SearchHistory search={item} />;
        case 'bank-transaction':
            return <BankTransaction transaction={item} />;
        case 'location-data':
            return <LocationData location={item} />;
        case 'email':
            return <Email email={item} />;
        case 'voicemail':
            return <Voicemail voicemail={item} />;
        default:
            return null;
    }
};

// This component is unused, commenting out to fix ESLint warning
// const EvidenceGroup = ({ items, title = "Evidence" }) => {
//   if (!items || items.length === 0) return null;
//   
//   return (
//     <div className="evidence-group">
//       <h3 className="evidence-group-title">{title}</h3>
//       <div className="evidence-items">
//         {items.map((item, index) => (
//           <EvidenceItem key={`evidence-${index}`} item={item} />
//         ))}
//       </div>
//     </div>
//   );
// };

function App({ skipSplash = false }) {
    const [storyData, setStoryData] = useState(null);
    const [selectedSuspectIndex, setSelectedSuspectIndex] = useState(null);
    const [investigationRevealed, setInvestigationRevealed] = useState(false);
    const [showAdditionalContext, setShowAdditionalContext] = useState(false);
    const [guess, setGuess] = useState(null); // { name: string, status: 'pending' | 'correct' | 'incorrect' | null }
    const [showSolution, setShowSolution] = useState(false);
    const [accusedSuspect, setAccusedSuspect] = useState(null); // Store the accused suspect
    const [showConfirmation, setShowConfirmation] = useState(false); // Control confirmation display
    const [guessedSuspects, setGuessedSuspects] = useState([]); // Track already guessed suspects
    const [userSelectedViewSolution, setUserSelectedViewSolution] = useState(false); // Track if user chose to view solution
    const [showSplash, setShowSplash] = useState(!skipSplash); // Control splash screen visibility
    const [animateStory, setAnimateStory] = useState(skipSplash); // Control story animation

    useEffect(() => {
        // Parse the story data once on component mount
        setStoryData(parseStory(rawStoryText));
    }, []);

    // Handle starting the game from splash screen
    const handleStartGame = () => {
        setShowSplash(false);
        // Set animate story to true when transitioning from splash screen
        setAnimateStory(true);
    };

    const handleSelectSuspect = (index) => {
        // Simply set the selected suspect index - no restrictions
        setSelectedSuspectIndex(index);
        // Still set investigationRevealed to true if it's the first time
        if (!investigationRevealed) {
            setInvestigationRevealed(true);
        }
        
        // Scroll to the investigation section after it appears
        setTimeout(() => {
            const investigationSection = document.querySelector('.investigation-section');
            if (investigationSection) {
                investigationSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300); // Wait a bit longer to allow the section to animate in
    };

    // New function to reset the investigation view
    const handleResetInvestigation = () => {
        setSelectedSuspectIndex(null);
        
        // Add smooth scrolling to the suspects section
        setTimeout(() => {
            const suspectsSection = document.querySelector('.suspects-section');
            if (suspectsSection) {
                suspectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100); // Small delay to ensure state update before scrolling
    };

    const handleRevealContext = () => {
        setShowAdditionalContext(true);
    };

    // Updated function to initiate accusation
    const initiateAccusation = (suspectName) => {
        setAccusedSuspect(suspectName);
        setShowConfirmation(true);
        
        // Add smooth scrolling to the confirmation section
        setTimeout(() => {
            const confirmationSection = document.querySelector('.accusation-confirm-inline');
            if (confirmationSection) {
                confirmationSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
                // If confirmation section isn't found, scroll to the guess section instead
                const guessSection = document.querySelector('.guess-section');
                if (guessSection) {
                    guessSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }, 100); // Small delay to ensure the confirmation section is rendered
    };

    // Updated function to cancel accusation
    const cancelAccusation = () => {
        setAccusedSuspect(null);
        setShowConfirmation(false);
    };

    // Reset guess to allow making another accusation
    const handleGuessAgain = () => {
        setGuess(null);
        setAccusedSuspect(null);
        setShowConfirmation(false);
    };

    // Show solution directly
    const handleViewSolution = () => {
        setShowSolution(true);
        setUserSelectedViewSolution(true);
        
        // Add smooth scrolling to the solution section
        setTimeout(() => {
            const solutionSection = document.querySelector('.solution-section');
            if (solutionSection) {
                solutionSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300); // Small delay to ensure solution section is rendered before scrolling
    };

    const handleGuess = (suspectName) => {
        // Reset the confirmation state
        setShowConfirmation(false);

        // Normalize names by trimming whitespace before comparison
        const normalizedSuspectName = suspectName?.trim();
        const normalizedMurderer = storyData?.murderer?.trim();
        const isCorrect = normalizedSuspectName === normalizedMurderer;
        
        // Add current suspect to guessed suspects list
        setGuessedSuspects(prev => [...prev, suspectName]);
        
        setGuess({ name: suspectName, status: 'pending' });

        // Start animation / delay - now longer and more dramatic
        setTimeout(() => {
            setGuess({ name: suspectName, status: isCorrect ? 'correct' : 'incorrect' });
            
            // For correct guess
            if (isCorrect) {
                // Reveal solution after animation finishes
                setTimeout(() => {
                    setShowSolution(true);
                    setUserSelectedViewSolution(true); // Also set this flag for correct guesses
                    
                    // Add smooth scrolling to the solution section after a correct guess
                    setTimeout(() => {
                        const solutionSection = document.querySelector('.solution-section');
                        if (solutionSection) {
                            solutionSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }, 300); // Small delay to ensure solution section is rendered
                }, 3000); // Longer duration for more drama
            } 
            // For incorrect guess
            else {
                // Scroll to the dramatic reveal section to show the "Accuse Another" and "View Solution" buttons
                setTimeout(() => {
                    const dramaticReveal = document.querySelector('.dramatic-reveal');
                    if (dramaticReveal) {
                        dramaticReveal.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 1000); // Scroll after animation has had time to complete
            }
        }, 2000); // Longer initial delay for suspense
    };

    const getGuessButtonClass = (suspectName) => {
        // Check if this suspect has been guessed before
        if (guessedSuspects.includes(suspectName)) {
            return 'already-guessed';
        }
        
        if (!guess) {
            if (accusedSuspect === suspectName && showConfirmation) return 'accused-suspect';
            return '';
        }
        if (guess.name !== suspectName) return 'guess-disabled'; // Grey out others

        switch (guess.status) {
            case 'pending':
                return 'guess-pending';
            case 'correct':
                return 'guess-correct';
            case 'incorrect':
                return 'guess-incorrect';
            default:
                return '';
        }
    };


    if (!storyData) {
        return <div>Loading Story...</div>;
    }

    // Show splash screen if showSplash is true
    if (showSplash) {
        return <SplashScreen onStart={handleStartGame} />;
    }

    return (
        <div className={`App ${animateStory ? 'animate-in' : ''}`}>
            <div 
                className="background-logo"
                style={{ backgroundImage: `url(${splashLogo})` }}
            ></div>
            <h1 className={`story-title ${animateStory ? 'animate-in' : ''}`}>{storyData.title}</h1>

            <div className={`story-section ${animateStory ? 'animate-in' : ''}`}>
                <StoryRenderer 
                    text={storyData.mainText} 
                    clue={storyData.clue} 
                    evidence={storyData.mainEvidence}
                />
            </div>

            {/* Additional Context Button - placed right after the story */}
            {storyData.additionalContext && (
                <div className={`story-section additional-context-container ${animateStory ? 'animate-in' : ''}`}>
                    {!showAdditionalContext ? (
                        <button onClick={handleRevealContext} className="reveal-context-button">
                            <span className="context-arrow"></span> Additional Context
                        </button>
                    ) : (
                        <div className="context-section">
                            <h2 className="context-header" onClick={() => setShowAdditionalContext(false)}>
                                <span className="context-arrow expanded"></span> Additional Context
                            </h2>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{storyData.additionalContext}</p>
                        </div>
                    )}
                </div>
            )}

            <div className={`story-section suspects-section ${animateStory ? 'animate-in' : ''}`}>
                <h2>Suspects</h2>
                <p className="subject-text">Click on a suspect to investigate:</p>
                <div className="suspect-buttons buttons">
                    {storyData.suspects.map((suspect, index) => (
                        <button
                            key={suspect.name}
                            onClick={() => handleSelectSuspect(index)}
                            // No disabled attribute - can select any suspect at any time
                            className={selectedSuspectIndex === index ? 'selected' : ''}
                        >
                            {suspect.name}
                        </button>
                    ))}
                </div>
            </div>

            {selectedSuspectIndex !== null && (
                <div className={`story-section investigation-section ${animateStory ? 'animate-in' : ''}`}>
                    <h3>Investigation: {storyData.suspects[selectedSuspectIndex].name}</h3>
                    
                    {/* Use InvestigationRenderer for investigation content */}
                    <InvestigationRenderer 
                        text={storyData.suspects[selectedSuspectIndex].investigation}
                        evidence={storyData.suspects[selectedSuspectIndex].evidence}
                    />
                    
                    {/* New "Investigate someone else" button */}
                    <div className="buttons">
                        <button onClick={handleResetInvestigation}>
                            Investigate someone else
                        </button>
                    </div>
                </div>
            )}

            {/* Make Your Accusation section - always visible */}
            <div className={`story-section guess-section ${animateStory ? 'animate-in' : ''}`}>
                <h2>Make Your Accusation</h2>
                
                {/* Accusation buttons - only available if solution is not shown */}
                {!showSolution && !guess && (
                    <div className="guess-buttons buttons">
                        {storyData.suspects.map((suspect) => (
                            <button
                                key={suspect.name}
                                onClick={() => !showConfirmation && initiateAccusation(suspect.name)}
                                disabled={(showConfirmation && accusedSuspect !== suspect.name) || guessedSuspects.includes(suspect.name)} 
                                className={`guess-button ${getGuessButtonClass(suspect.name)}`}
                            >
                                Accuse {suspect.name}
                                {guessedSuspects.includes(suspect.name) && ' ✓'}
                            </button>
                        ))}
                    </div>
                )}
                
                {/* Inline confirmation - only if not solution and confirmation is active */}
                {!showSolution && showConfirmation && (
                    <div className="accusation-confirm-inline">
                        <p>Are you sure you want to accuse <strong>{accusedSuspect}</strong>?</p>
                        <div className="confirm-buttons buttons">
                            <button 
                                onClick={cancelAccusation}
                                className="cancel-button"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => handleGuess(accusedSuspect)} 
                                className="confirm-button"
                            >
                                Confirm Accusation
                            </button>
                        </div>
                    </div>
                )}
                
                {/* Dramatic reveal - always visible once a guess is made */}
                {guess && (
                    <div className={`dramatic-reveal ${guess.status} ${showSolution ? 'with-solution' : ''}`}>
                        {guess.status === 'pending' && 
                            <div className="pending-message">
                                <p>Examining the evidence...</p>
                            </div>
                        }
                        {guess.status === 'correct' && 
                            <div className="result-message">
                                <p>You've solved the mystery!</p>
                                {showSolution && (
                                    <div className="post-guess-options buttons">
                                        <button 
                                            className="view-solution-button correct-solution selected-action"
                                            disabled={true}
                                        >
                                            Solution Revealed
                                        </button>
                                    </div>
                                )}
                            </div>
                        }
                        {guess.status === 'incorrect' && 
                            <div className="result-message">
                                <p>The true culprit remains at large...</p>
                                {!showSolution && (
                                    <div className="post-guess-options buttons">
                                        <button 
                                            onClick={handleGuessAgain} 
                                            className="guess-again-button"
                                            disabled={showSolution}
                                        >
                                            Accuse Another
                                        </button>
                                        <button 
                                            onClick={handleViewSolution} 
                                            className="view-solution-button"
                                            disabled={showSolution}
                                        >
                                            View Solution
                                        </button>
                                    </div>
                                )}
                                {showSolution && userSelectedViewSolution && (
                                    <div className="post-guess-options buttons">
                                        <button 
                                            className="view-solution-button selected-action"
                                            disabled={true}
                                        >
                                            Solution Revealed
                                        </button>
                                    </div>
                                )}
                            </div>
                        }
                    </div>
                )}
            </div>

            {showSolution && storyData.solution && (
                <div className={`story-section solution-section ${animateStory ? 'animate-in' : ''}`}>
                    <h2>From The Victim's Perspective</h2>
                    
                    {/* Use SolutionRenderer for solution content */}
                    <SolutionRenderer 
                        text={storyData.solution}
                        evidence={storyData.solutionEvidence}
                    />
                    
                    <div className="murderer">
                        {storyData.murderer ? (
                            <p><strong>The murderer was {storyData.murderer}.</strong></p>
                        ) : (
                            <p><em>No murderer was identified in the story data.</em></p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;